import { useEffect, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { LogOut, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtError = (d) => (typeof d === "string" ? d : "Error. Intente nuevamente.");

const TIPO_BADGE = {
  cotizacion: "bg-certero/15 text-certero border-certero/40",
  soporte: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  emergencia: "bg-red-500/20 text-red-300 border-red-500/50",
  academia: "bg-sky-500/15 text-sky-300 border-sky-500/40",
  servicio: "bg-violet-500/15 text-violet-300 border-violet-500/40",
  campana: "bg-teal-500/15 text-teal-300 border-teal-500/40",
  repuesto: "bg-orange-500/15 text-orange-300 border-orange-500/40",
  contacto: "bg-slate-500/15 text-slate-300 border-slate-500/40",
  demo: "bg-pink-500/15 text-pink-300 border-pink-500/40",
};

export default function Admin() {
  const [user, setUser] = useState(undefined); // undefined: verificando
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("leads");
  const [students, setStudents] = useState(null);

  const loadStudents = async () => {
    try {
      const { data: d } = await axios.get(`${API}/admin/students`, { withCredentials: true });
      setStudents(d);
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail));
    }
  };

  const checkSession = async () => {
    try {
      let me = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (!me.ok) {
        const r = await fetch(`${API}/auth/refresh`, { method: "POST", credentials: "include" });
        if (r.ok) me = await fetch(`${API}/auth/me`, { credentials: "include" });
      }
      setUser(me.ok);
      if (me.ok) { loadLeads(); loadStudents(); }
    } catch {
      setUser(false);
    }
  };

  const loadLeads = async () => {
    try {
      const { data: d } = await axios.get(`${API}/admin/leads`, { withCredentials: true });
      setData(d);
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail));
      if (e.response?.status === 401) setUser(false);
    }
  };

  useEffect(() => {
    checkSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/login`, creds, { withCredentials: true });
      setUser(true);
      toast.success("Sesión iniciada");
      loadLeads();
      loadStudents();
    } catch (err) {
      toast.error(fmtError(err.response?.data?.detail));
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignorar */ }
    setUser(false);
    setData(null);
  };

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-abyss">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-smoke">Verificando sesión...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-abyss px-4">
        <form
          data-testid="admin-login-form"
          onSubmit={login}
          className="w-full max-w-sm border border-hud bg-surface p-8"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-certero">CERTERO SRL</p>
          <h1 className="mt-2 font-display text-xl font-bold uppercase tracking-wide text-mist">
            Panel interno
          </h1>
          <div className="mt-7 flex flex-col gap-4">
            <input
              data-testid="admin-login-email"
              type="email"
              placeholder="Correo"
              value={creds.email}
              onChange={(e) => setCreds({ ...creds, email: e.target.value })}
              className="h-12 w-full border border-hud bg-abyss px-4 text-sm text-mist placeholder:text-smoke/60 outline-none focus:border-certero"
              required
            />
            <input
              data-testid="admin-login-password"
              type="password"
              placeholder="Contraseña"
              value={creds.password}
              onChange={(e) => setCreds({ ...creds, password: e.target.value })}
              className="h-12 w-full border border-hud bg-abyss px-4 text-sm text-mist placeholder:text-smoke/60 outline-none focus:border-certero"
              required
            />
            <button
              data-testid="admin-login-submit"
              className="h-12 w-full rounded-full bg-certero font-display text-xs font-bold uppercase tracking-[0.15em] text-ink transition-colors duration-300 hover:bg-[#4bc443]"
            >
              Ingresar
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-abyss px-4 py-10 sm:px-8" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-certero">CERTERO SRL</p>
            <h1 className="mt-1 font-display text-2xl font-bold uppercase tracking-wide text-mist">
              Solicitudes web
            </h1>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="admin-refresh"
              onClick={loadLeads}
              className="inline-flex h-10 items-center gap-2 rounded-full border border-hud px-5 font-mono text-[11px] uppercase tracking-[0.15em] text-mist/80 transition-colors duration-300 hover:border-certero hover:text-certero"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Actualizar
            </button>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex h-10 items-center gap-2 rounded-full border border-hud px-5 font-mono text-[11px] uppercase tracking-[0.15em] text-mist/80 transition-colors duration-300 hover:border-red-500 hover:text-red-400"
            >
              <LogOut className="h-3.5 w-3.5" /> Salir
            </button>
          </div>
        </div>

        {data && (
          <>
            <div className="mt-8 flex gap-2">
              {[["leads", "Solicitudes web"], ["academy", "Academy — estudiantes"]].map(([id, label]) => (
                <button
                  key={id}
                  data-testid={`admin-tab-${id}`}
                  onClick={() => setTab(id)}
                  className={`h-10 rounded-full px-6 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                    tab === id ? "bg-certero text-ink" : "border border-hud text-mist/70 hover:border-certero hover:text-certero"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {tab === "leads" && (
              <>
            <div className="mt-8 flex flex-wrap gap-3">
              <span data-testid="admin-stat-total" className="border border-hud bg-surface px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-mist">
                Total: {data.total}
              </span>
              {Object.entries(data.counts).map(([t, n]) => (
                <span key={t} className={`border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] ${TIPO_BADGE[t] || TIPO_BADGE.contacto}`}>
                  {t}: {n}
                </span>
              ))}
            </div>

            {data.leads.length === 0 ? (
              <p className="mt-10 border border-dashed border-hud p-10 text-center text-sm text-smoke" data-testid="admin-leads-empty">
                Aún no hay solicitudes. Los formularios de la web aparecerán aquí.
              </p>
            ) : (
              <div className="mt-8 overflow-x-auto border border-hud">
                <table className="w-full min-w-[900px] text-left text-sm" data-testid="admin-leads-table">
                  <thead>
                    <tr className="border-b border-hud bg-surface font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">
                      <th className="p-4">Fecha</th>
                      <th className="p-4">Tipo</th>
                      <th className="p-4">Nombre</th>
                      <th className="p-4">Contacto</th>
                      <th className="p-4">Interés</th>
                      <th className="p-4">Mensaje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.leads.map((l) => (
                      <tr key={l.id} className="border-b border-hud/50 bg-abyss align-top transition-colors duration-200 hover:bg-surface/60">
                        <td className="p-4 font-mono text-xs text-smoke">
                          {new Date(l.creado_en).toLocaleString("es-BO")}
                        </td>
                        <td className="p-4">
                          <span className={`border px-2 py-1 font-mono text-[10px] uppercase tracking-[0.1em] ${TIPO_BADGE[l.tipo] || TIPO_BADGE.contacto}`}>
                            {l.tipo}
                          </span>
                        </td>
                        <td className="p-4 text-mist">{l.nombre}</td>
                        <td className="p-4 text-smoke">
                          {l.telefono}
                          {l.email && <span className="block text-xs">{l.email}</span>}
                        </td>
                        <td className="p-4 text-smoke">{l.interes || "—"}</td>
                        <td className="max-w-[280px] p-4 text-smoke">{l.mensaje || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
              </>
            )}

            {tab === "academy" && students && (
              <div className="mt-8" data-testid="admin-academy-panel">
                <h2 className="font-display text-lg font-bold uppercase tracking-wide text-mist">Estudiantes del curso virtual</h2>
                {students.students.length === 0 ? (
                  <p className="mt-5 border border-dashed border-hud p-10 text-center text-sm text-smoke" data-testid="admin-students-empty">
                    Aún no hay estudiantes registrados.
                  </p>
                ) : (
                  <div className="mt-5 overflow-x-auto border border-hud">
                    <table className="w-full min-w-[760px] text-left text-sm" data-testid="admin-students-table">
                      <thead>
                        <tr className="border-b border-hud bg-surface font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">
                          <th className="p-4">Fecha</th>
                          <th className="p-4">Nombre</th>
                          <th className="p-4">Correo</th>
                          <th className="p-4">Estado</th>
                          <th className="p-4">Origen</th>
                          <th className="p-4">Acción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {students.students.map((s) => (
                          <tr key={s._id} className="border-b border-hud/50 bg-abyss align-top">
                            <td className="p-4 font-mono text-xs text-smoke">{new Date(s.creado_en).toLocaleDateString("es-BO")}</td>
                            <td className="p-4 text-mist">{s.nombre}</td>
                            <td className="p-4 text-smoke">{s.email}</td>
                            <td className="p-4">
                              <span className={`border px-2 py-1 font-mono text-[10px] uppercase tracking-[0.1em] ${s.pagado ? "border-certero/50 bg-certero/15 text-certero" : "border-dashed border-smoke/50 text-smoke"}`} data-testid={`admin-student-estado-${s._id}`}>
                                {s.pagado ? "ACCESO ACTIVO" : "PENDIENTE DE PAGO"}
                              </span>
                            </td>
                            <td className="p-4 font-mono text-xs text-smoke">{s.fuente || "—"}</td>
                            <td className="p-4">
                              {!s.pagado && (
                                <button
                                  data-testid={`admin-student-activar-${s._id}`}
                                  onClick={async () => {
                                    try {
                                      await axios.patch(`${API}/admin/students/${s._id}/activar`, {}, { withCredentials: true });
                                      toast.success("Acceso activado");
                                      loadStudents();
                                    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
                                  }}
                                  className="rounded-full bg-certero px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-ink transition-colors duration-300 hover:bg-[#4bc443]"
                                >
                                  Activar acceso (QR)
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <h2 className="mt-10 font-display text-lg font-bold uppercase tracking-wide text-mist">Pagos (Stripe)</h2>
                {students.transactions.length === 0 ? (
                  <p className="mt-5 border border-dashed border-hud p-8 text-center text-sm text-smoke" data-testid="admin-transactions-empty">
                    Aún no hay pagos registrados.
                  </p>
                ) : (
                  <div className="mt-5 overflow-x-auto border border-hud">
                    <table className="w-full min-w-[640px] text-left text-sm" data-testid="admin-transactions-table">
                      <thead>
                        <tr className="border-b border-hud bg-surface font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">
                          <th className="p-4">Fecha</th>
                          <th className="p-4">Monto</th>
                          <th className="p-4">Estado</th>
                          <th className="p-4">Sesión</th>
                        </tr>
                      </thead>
                      <tbody>
                        {students.transactions.map((t) => (
                          <tr key={t.session_id} className="border-b border-hud/50 bg-abyss">
                            <td className="p-4 font-mono text-xs text-smoke">{new Date(t.created_at).toLocaleString("es-BO")}</td>
                            <td className="p-4 font-mono text-xs text-mist">${t.amount} {t.currency?.toUpperCase()}</td>
                            <td className="p-4 font-mono text-xs">
                              <span className={t.payment_status === "paid" ? "text-certero" : "text-amber-300"}>{t.payment_status}</span>
                            </td>
                            <td className="max-w-[220px] truncate p-4 font-mono text-xs text-smoke">{t.session_id}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
